/**
 * The visitor's position WITHOUT a button, when the browser has already let the page have it (owner, QA 24.10.2026:
 * «геопозиция разрешена, а гид отвечает "я не знаю, где вы"» — `navigator.geolocation` was only ever asked by a tap
 * on «Определить моё место»).
 *
 * Before a turn of the Agent family goes out (`App.dispatch`): when `navigator.permissions` says `granted` and the
 * session has no fresh position of the GPS kind, ask the browser once — `timeout` 3 s, a fix up to 2 min old will do —
 * and commit it the way the button does (`setAgentGeo`, source `gps`, named by 2GIS Geocoder if that does not hold
 * the turn past the same budget). Everything else sends as before, at once:
 * - `prompt`, `denied`, no Permissions API, an error or a timeout — no request for permission is ever made here
 *   (UI_CONTRACT §4.2: the browser's own dialog comes from the visitor's tap, not from a message);
 * - a place the visitor gave — an address, a point on the map, an entrance, a landmark said in the chat — and the
 *   composer's own GPS stand: an automatic fix never replaces them;
 * - a place the visitor cleared or erased (`locationResetAt`) is not taken again until they ask for it;
 * - a fix coarser than `APPROXIMATE_M` is not committed silently — the button asks first, so does the card;
 * - after a failed, late or coarse fix the browser is not asked again for `AUTO_RETRY_MS` (5 min, this page's memory) — a
 *   phone indoors would hold every message for the whole timeout;
 * - a renewed fix within `SAME_PLACE_M` of the held GPS position only renews its time (`touchAgentGeo`): `setAgentGeo`
 *   resets the route card, and a card open on the screen must not vanish with every message.
 */
import { isAgentFamily } from '../store/graphFamily';
import type { GeoMeta } from '../store/sessionStore';
import { distanceMeters } from './navigation';
import { APPROXIMATE_M } from './reverseGeocode';

/** The wait for the browser's fix; past it the turn goes out without one. */
export const AUTO_TIMEOUT_MS = 3000;
/** A fix this old is as good as a new one for a walk in a park (`maximumAge` of the browser's request). */
export const AUTO_MAX_AGE_MS = 120_000;
/** The session's GPS position stays fresh this long (10 min); then the next turn renews it. */
export const GPS_FRESH_MS = 10 * 60_000;
/** A renewed fix within this distance of the position the session holds is the same place: the position is kept,
 *  only its time and accuracy are renewed — a commit would also close the route card open on the screen. */
export const SAME_PLACE_M = 100;
/** The longest the turn waits for the point's NAME on top of the fix, and never past the total budget. */
export const AUTO_LABEL_MS = 1500;
const PERMISSION_MS = 500;
/** After a fix that failed, came too late or too coarse the browser is not asked again for this long: a phone indoors
 *  would otherwise hold every message for the whole timeout. */
export const AUTO_RETRY_MS = 5 * 60_000;

/** When the last attempt failed (this page's memory; a reload starts clean) — `null` after a success or none. */
let failedAt: number | null = null;
/** For tests: forget a failed attempt. */
export function forgetAutoLocateFailure(): void { failedAt = null; }

export type AutoSession = {
  assistantId: string;
  geo: { lat: number; lon: number } | null;
  /** The position of the Agent's own place (`agentGeoSet`): the held GPS fix a renewed one is compared with. */
  agentGeo: { lat: number; lon: number } | null;
  agentGeoSet: boolean;
  agentGeoMeta: GeoMeta | null;
  locationResetAt: number | null;
};

/** A place that is the visitor's own doing, or a deliberate absence of one: an automatic fix leaves it alone. */
function ownPlace(session: AutoSession): boolean {
  if (session.agentGeoSet && session.agentGeoMeta) return session.agentGeoMeta.source !== 'gps';
  if (session.geo) return true; // the composer's «Показать, что рядом со мной»
  return session.locationResetAt != null; // cleared in the panel or erased — their decision
}

/** Is there a reason to ask the browser: an Agent graph, no place of the visitor's own, no GPS position younger than
 *  `GPS_FRESH_MS`, and no failed attempt in the last `AUTO_RETRY_MS`. */
export function wantsAutoGps(session: AutoSession, now: number): boolean {
  if (!isAgentFamily(session.assistantId) || ownPlace(session)) return false;
  if (failedAt !== null && now - failedAt < AUTO_RETRY_MS) return false;
  const meta = session.agentGeoMeta;
  return !(session.agentGeoSet && meta && now - meta.at <= GPS_FRESH_MS);
}

type Deps = {
  session: () => AutoSession;
  permissions: Pick<Permissions, 'query'> | undefined;
  geolocation: Pick<Geolocation, 'getCurrentPosition'> | undefined;
  setAgentGeo: (point: { lat: number; lon: number }, meta: Partial<GeoMeta>) => void;
  /** Renews the time and accuracy of the position the session holds — no new place, nothing on the screen reset. */
  touchAgentGeo: (meta: Pick<GeoMeta, 'at' | 'accuracyM'>) => void;
  /** 2GIS Geocoder (`reverseGeocode`): the point's address or `null`. */
  reverse?: (lat: number, lon: number) => Promise<string | null>;
  now?: () => number;
};

const delay = <T>(ms: number, value: T) => new Promise<T>((resolve) => { setTimeout(() => resolve(value), ms); });

async function granted(permissions: Deps['permissions']): Promise<boolean> {
  if (!permissions?.query) return false;
  try {
    const status = await Promise.race([permissions.query({ name: 'geolocation' as PermissionName }), delay(PERMISSION_MS, null)]);
    return status?.state === 'granted';
  } catch {
    return false;
  }
}

function fix(geolocation: NonNullable<Deps['geolocation']>): Promise<GeolocationPosition | null> {
  return new Promise((resolve) => {
    // some browsers never call back when the page is hidden mid-request: the turn must not wait for them
    const giveUp = setTimeout(() => resolve(null), AUTO_TIMEOUT_MS + 500);
    const done = (position: GeolocationPosition | null) => { clearTimeout(giveUp); resolve(position); };
    try {
      geolocation.getCurrentPosition(done, () => done(null), { enableHighAccuracy: true, timeout: AUTO_TIMEOUT_MS, maximumAge: AUTO_MAX_AGE_MS });
    } catch {
      done(null);
    }
  });
}

/** Take the browser's position into the session when the rules above allow it. → whether a position was committed. */
export async function autoLocate({ session, permissions, geolocation, setAgentGeo, touchAgentGeo, reverse, now = Date.now }: Deps): Promise<boolean> {
  const started = now();
  if (!geolocation || !wantsAutoGps(session(), started) || !(await granted(permissions))) return false;
  const position = await fix(geolocation);
  if (!position || position.coords.accuracy > APPROXIMATE_M) { failedAt = now(); return false; }
  failedAt = null;
  const { latitude: lat, longitude: lon, accuracy } = position.coords;
  const named = reverse ? Promise.race([reverse(lat, lon).catch(() => null), delay(Math.max(0, Math.min(AUTO_LABEL_MS, AUTO_TIMEOUT_MS - (now() - started))), null)]) : Promise.resolve(null);
  const label = await named;
  // the visitor may have chosen a place while the browser was answering; the same fix is not committed twice
  const current = session();
  if (ownPlace(current)) return false;
  if (current.agentGeoSet && current.agentGeoMeta && current.agentGeoMeta.at >= position.timestamp) return false;
  const held = current.agentGeoSet && current.agentGeoMeta?.source === 'gps' ? current.agentGeo : null;
  if (held && distanceMeters(held, { lat, lon }) < SAME_PLACE_M) {
    touchAgentGeo({ at: position.timestamp, accuracyM: accuracy });
    return true;
  }
  setAgentGeo({ lat, lon }, { source: 'gps', at: position.timestamp, accuracyM: accuracy, label });
  return true;
}
